import React, { useState } from 'react';
import { Download, ImageOff, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const Field = ({ label, value, mono, wide, testId }) => (
  <div className={wide ? 'col-span-full' : ''} data-testid={testId}>
    <dt className="text-[11px] font-medium uppercase tracking-wider text-slate-400">{label}</dt>
    <dd className={`mt-1 text-sm font-medium text-slate-900 break-words ${mono ? 'font-mono' : ''}`}>{value || '—'}</dd>
  </div>
);

const Group = ({ title, cols = 'sm:grid-cols-3', children }) => (
  <section className="rounded-xl border border-slate-200 bg-white">
    <h4 className="border-b border-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</h4>
    <dl className={`grid grid-cols-2 ${cols} gap-x-6 gap-y-4 p-4`}>{children}</dl>
  </section>
);

const ImageCard = ({ label, src, onOpen, action, testId }) => (
  <div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={testId}>
    <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
      {action}
    </div>
    {src ? (
      <button type="button" onClick={() => onOpen({ src, label })} className="group relative h-44 bg-slate-50">
        <img src={src} alt={label} className="h-full w-full object-contain p-2" />
        <span className="absolute bottom-2 right-2 rounded-md bg-white/90 p-1 opacity-0 shadow transition-opacity group-hover:opacity-100">
          <Maximize2 className="h-3.5 w-3.5 text-slate-600" />
        </span>
      </button>
    ) : (
      <div className="flex h-44 flex-col items-center justify-center gap-1 bg-slate-50 text-xs text-slate-400">
        <ImageOff className="h-5 w-5" /> Not available yet
      </div>
    )}
  </div>
);

export const EANRequestDetails = ({ req, onDownloadBarcode }) => {
  const [preview, setPreview] = useState(null);
  const g = (v) => (v ? `${v} g` : '');
  return (
    <div className="space-y-4" data-testid="ean-request-details">
      {(req.ean_number || req.assigned_sku) && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {req.ean_number && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-[11px] font-medium uppercase tracking-wider text-emerald-700">Generated EAN</p>
              <p className="mt-1 font-mono text-xl font-bold text-emerald-800" data-testid="ean-view-ean-number">{req.ean_number}</p>
            </div>
          )}
          {req.assigned_sku && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 p-4">
              <p className="text-[11px] font-medium uppercase tracking-wider text-sky-700">Assigned SKU</p>
              <p className="mt-1 font-mono text-lg font-semibold text-sky-800" data-testid="ean-view-sku">{req.assigned_sku}</p>
            </div>
          )}
        </div>
      )}

      <Group title="Product">
        <Field label="Product Name" value={req.product_name} />
        <Field label="Flavour" value={req.flavour} />
        <Field label="Brand" value={req.brand_name} />
        <Field label="Country of Origin" value={req.country_of_origin} />
        <Field label="Product Description" value={req.product_description} wide testId="ean-view-description" />
      </Group>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Group title="Pricing & Weights" cols="sm:grid-cols-3">
          <Field label="MRP" value={req.mrp ? `₹${req.mrp}` : ''} />
          <Field label="Gross Weight" value={g(req.gross_weight_grams)} />
          <Field label="Net Weight" value={g(req.net_weight_grams)} />
        </Group>
        <Group title="Compliance & Tax" cols="sm:grid-cols-2">
          <Field label="HSN Code" value={req.hsn_code} mono />
          <Field label="Tax Rate" value={req.tax_rate} />
        </Group>
      </div>

      <Group title="Storage" cols="sm:grid-cols-1">
        <Field label="Storage Condition" value={req.storage_condition} wide testId="ean-view-storage" />
      </Group>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <ImageCard label="Front Image" src={req.front_image} onOpen={setPreview} testId="ean-view-front-card" />
        <ImageCard label="Back Image" src={req.back_image} onOpen={setPreview} testId="ean-view-back-card" />
        <ImageCard
          label="Barcode Label"
          src={req.barcode_image}
          onOpen={setPreview}
          testId="ean-view-barcode-card"
          action={req.barcode_image && (
            <Button variant="ghost" size="sm" onClick={() => onDownloadBarcode(req)} title="Download barcode" className="h-6 w-6 p-0 text-[#2EB4AC] hover:bg-[#2EB4AC]/10" data-testid="ean-view-download-barcode">
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
        />
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="ean-view-image-preview">
          <DialogHeader><DialogTitle>{preview?.label}</DialogTitle></DialogHeader>
          {preview && <img src={preview.src} alt={preview.label} className="max-h-[70vh] w-full rounded-lg bg-slate-50 object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
